import { Descriptions, Grid, Tabs } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type ConfiguratorModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/ConfiguratorModuleWithPermissionsSpecs';
import { ContentRefinementSourceAction, ContentRefinementSourceKind } from '../../../../api/types';
import { GenericStateOnPageContextProvider } from '../../../../components/contexts/GenericStateOnPageContextManager';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import ConfiguratorModuleActions from './ConfiguratorModuleActions';
import ConfiguratorModuleFooter from './ConfiguratorModuleFooter';
import ConfiguratorModuleInnerPage from './ConfiguratorModuleInnerPage';
import { HeaderTabs } from './type';

export type ConfiguratorModulePageProps = { module: ConfiguratorModuleWithPermissionsSpecsFragment };

const ConfiguratorModulePage = ({ module }: ConfiguratorModulePageProps) => {
    const { t } = useTranslation(['configuratorModuleDetail']);
    const navigate = useNavigate();

    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const [activeTab, setActiveTab] = useState(HeaderTabs.MainDetails);

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const screens = Grid.useBreakpoint();
    const refineContentParams = useMemo(
        () => ({
            hasPermissionToRefineContent: permissions.hasUpdatePermission,
            source: {
                kind: ContentRefinementSourceKind.Module,
                action: ContentRefinementSourceAction.Update,
                identifier: module.id,
            },
        }),
        [module.id, permissions.hasUpdatePermission]
    );

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('configuratorModuleDetail:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('configuratorModuleDetail:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('configuratorModuleDetail:content.vehicleModule')}>
                {module.vehicleModule.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('configuratorModuleDetail:content.customerModule')}>
                {`${module.customerModule.displayName} - ${module.kycPresets
                    .map(kycPreset => kycPreset.displayName)
                    .join(', ')}`}
            </Descriptions.Item>
            {module.bankModule && (
                <Descriptions.Item label={t('configuratorModuleDetail:content.financingModule')}>
                    {module.bankModule.displayName}
                </Descriptions.Item>
            )}
            {module.bankModule ? (
                <Descriptions.Item label={t('configuratorModuleDetail:content.consentModule')}>
                    {module.agreementsModule.displayName}
                </Descriptions.Item>
            ) : null}
            {module.market ? (
                <Descriptions.Item label={t('configuratorModuleDetail:content.market')}>
                    {module.market}
                </Descriptions.Item>
            ) : null}

            {module.appointmentModule ? (
                <Descriptions.Item label={t('configuratorModuleDetail:content.appointment')}>
                    {module.appointmentModule.displayName}
                </Descriptions.Item>
            ) : null}

            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <GenericStateOnPageContextProvider refineContent={refineContentParams}>
            <ConsolePageWithHeader
                content={content}
                extra={<ConfiguratorModuleActions module={module} permissions={permissions} />}
                footer={[<ConfiguratorModuleFooter activeTab={activeTab} />]}
                header={{
                    footer: (
                        <Tabs
                            activeKey={activeTab}
                            items={[
                                {
                                    label: t('configuratorModuleDetail:tabs.mainDetails'),
                                    key: HeaderTabs.MainDetails,
                                },
                                {
                                    label: t('configuratorModuleDetail:tabs.vehicles.title'),
                                    key: HeaderTabs.Vehicles,
                                },
                                {
                                    label: t('configuratorModuleDetail:tabs.financeProducts.title'),
                                    key: HeaderTabs.FinanceProducts,
                                },

                                {
                                    label: t('configuratorModuleDetail:tabs.email'),
                                    key: HeaderTabs.Email,
                                },
                            ]}
                            onChange={(activeKey: HeaderTabs) => setActiveTab(activeKey)}
                        />
                    ),
                }}
                onBack={() => navigate('/admin/system/modules')}
                title={
                    screens.md ? t('configuratorModuleDetail:title', { name: module.displayName }) : module.displayName
                }
            >
                <ConfiguratorModuleInnerPage activeTab={activeTab} module={module} />
            </ConsolePageWithHeader>
        </GenericStateOnPageContextProvider>
    );
};

export default ConfiguratorModulePage;
